import "./App.css";
import { ErrorBoundary } from './components/ErrorBoundary';
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { initAnalytics } from "./lib/analytics";
import Home from "./pages/Home";
const BookingCalendar = lazy(() => import("./pages/BookingCalendar"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Auth = lazy(() => import("./pages/Auth"));
const EmployeeDashboard = lazy(() => import("./pages/EmployeeDashboard"));
const CustomerDashboard = lazy(() => import("./pages/CustomerDashboard"));
const JobPhotos = lazy(() => import("./pages/JobPhotos"));
const InvoiceCreator = lazy(() => import("./pages/InvoiceCreator"));
const InvoiceDetailPage = lazy(() => import("./pages/InvoiceDetailPage"));
const TrackPage = lazy(() => import("./pages/TrackPage"));
const PriceCalculator = lazy(() => import("./pages/PriceCalculator"));
const EstimatePlatform = lazy(() => import("./pages/EstimatePlatform"));
const AccountingPlatform = lazy(() => import("./pages/AccountingPlatform"));
const ExteriorPaintingPage = lazy(() => import("./pages/ExteriorPaintingPage"));
const InteriorPaintingPage = lazy(() => import("./pages/InteriorPaintingPage"));
const AnalyticsDashboard = lazy(() => import("./pages/AnalyticsDashboard"));
const GeneralRepairsPage = lazy(() => import("./pages/GeneralRepairsPage"));
const PlumbingPage = lazy(() => import("./pages/PlumbingPage"));
const ElectricalPage = lazy(() => import("./pages/ElectricalPage"));
const CarpentryPage = lazy(() => import("./pages/CarpentryPage"));
const BuildingMaintenancePage = lazy(() => import("./pages/BuildingMaintenancePage"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const SmsTermsPage = lazy(() => import("./pages/SmsTermsPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ProjectPage = lazy(() => import("./pages/ProjectPage"));
import NotFoundPage from "./pages/NotFoundPage";
import GetAppPage from "./pages/GetAppPage";
import AdminEntry from "./pages/AdminEntry";
const ApproveEstimatePage = lazy(() => import("./pages/ApproveEstimatePage"));
const PayResultPage = lazy(() => import("./pages/PayResultPage"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const MediaKitPage = lazy(() => import("./pages/MediaKitPage"));
const VideoPage = lazy(() => import("./pages/VideoPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const FlyerPage = lazy(() => import("./pages/FlyerPage"));
const FlooringPage = lazy(() => import("./pages/FlooringPage"));
const StuccoPage = lazy(() => import("./pages/StuccoPage"));
const DrywallPage = lazy(() => import("./pages/DrywallPage"));
const RemodelingPage = lazy(() => import("./pages/RemodelingPage"));
const LandscapingPage = lazy(() => import("./pages/LandscapingPage"));
const FauxFinishesPage = lazy(() => import("./pages/FauxFinishesPage"));
const HaulingPage = lazy(() => import("./pages/HaulingPage"));
const MaterialDeliveryPage = lazy(() => import("./pages/MaterialDeliveryPage"));
const RioRanchoPage = lazy(() => import("./pages/RioRanchoPage"));
const SantaFePage = lazy(() => import("./pages/SantaFePage"));
const CorralesPage = lazy(() => import("./pages/CorralesPage"));
const BernalilloPage = lazy(() => import("./pages/BernalilloPage"));
const LosLunasPage = lazy(() => import("./pages/LosLunasPage"));
const PlacitasPage = lazy(() => import("./pages/PlacitasPage"));
const ResidentialHandymanPage = lazy(() => import("./pages/ResidentialHandymanPage"));
const CommercialHandymanPage = lazy(() => import("./pages/CommercialHandymanPage"));
const BathroomRemodelingPage = lazy(() => import("./pages/BathroomRemodelingPage"));
const DoorReplacementPage = lazy(() => import("./pages/DoorReplacementPage"));
const CommercialPropertyMaintenancePage = lazy(() => import("./pages/CommercialPropertyMaintenancePage"));
const RentalPropertyMaintenancePage = lazy(() => import("./pages/RentalPropertyMaintenancePage"));
const RealtorServicesPage = lazy(() => import("./pages/RealtorServicesPage"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));const ComebackReschedulePage = lazy(() => import("./pages/ComebackReschedulePage"));const HeyPaolo = lazy(() => import("./pages/HeyPaolo"));const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ForcePasswordChangePage = lazy(() => import("./pages/ForcePasswordChangePage"));
const FacilityMaintenancePage = lazy(() => import("./pages/FacilityMaintenancePage"));
const CommercialLandingPage = lazy(() => import("./pages/CommercialLandingPage"));
const ReviewRedirect = lazy(() => import("./pages/ReviewRedirect"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const FallReadyPage = lazy(() => import("./pages/FallReadyPage"));
const AiEstimatePage = lazy(() => import("./pages/AiEstimatePage"));
const EstimatorWizard = lazy(() => import("./pages/EstimatorWizard"));
const AdminEstimator = lazy(() => import("./pages/AdminEstimator"));
const ScopeView = lazy(() => import("./pages/ScopeView"));
const SeasonalFlyerPage = lazy(() => import("./pages/SeasonalFlyerPage"));
const PortalPage = lazy(() => import("./pages/PortalPage"));
import PortalMascot from "./components/PortalMascot";
import CallTextNow from "./components/CallTextNow";
const LocalServicePage = lazy(() => import("./components/LocalServicePage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-white" data-testid="route-loading">
    <div className="w-10 h-10 rounded-full border-4 border-slate-200 border-t-brand-blue animate-spin" />
  </div>
);

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/book" element={<BookingCalendar />} />
          <Route path="/booking" element={<BookingCalendar />} />
          <Route path="/aqh-mgmt-7816" element={<AdminDashboard />} />
          <Route path="/calculator" element={<PriceCalculator />} />
          <Route path="/estimates" element={<EstimatePlatform />} />
          <Route path="/estimates/new" element={<EstimatePlatform />} />
          <Route path="/estimates/:estimateId" element={<EstimatePlatform />} />
          <Route path="/accounting" element={<AccountingPlatform />} />
          <Route path="/exterior-painting" element={<ExteriorPaintingPage />} />
          <Route path="/interior-painting" element={<InteriorPaintingPage />} />
          <Route path="/general-repairs" element={<GeneralRepairsPage />} />
          <Route path="/plumbing" element={<PlumbingPage />} />
          <Route path="/electrical" element={<ElectricalPage />} />
          <Route path="/carpentry" element={<CarpentryPage />} />
          <Route path="/building-maintenance" element={<BuildingMaintenancePage />} />
          <Route path="/flooring" element={<FlooringPage />} />
          <Route path="/stucco" element={<StuccoPage />} />
          <Route path="/drywall" element={<DrywallPage />} />
          <Route path="/remodeling" element={<RemodelingPage />} />
          <Route path="/landscaping" element={<LandscapingPage />} />
          <Route path="/faux-finishes" element={<FauxFinishesPage />} />
          <Route path="/hauling" element={<HaulingPage />} />
          <Route path="/material-delivery" element={<MaterialDeliveryPage />} />
          <Route path="/rio-rancho" element={<RioRanchoPage />} />
          <Route path="/santa-fe" element={<SantaFePage />} />
          <Route path="/corrales" element={<CorralesPage />} />
          <Route path="/bernalillo" element={<BernalilloPage />} />
          <Route path="/los-lunas" element={<LosLunasPage />} />
          <Route path="/placitas" element={<PlacitasPage />} />
          <Route path="/residential-handyman" element={<ResidentialHandymanPage />} />
          <Route path="/commercial-handyman" element={<CommercialHandymanPage />} />
          <Route path="/bathroom-remodeling" element={<BathroomRemodelingPage />} />
          <Route path="/door-replacement" element={<DoorReplacementPage />} />
          <Route path="/commercial-property-maintenance" element={<CommercialPropertyMaintenancePage />} />
          <Route path="/rental-property-maintenance" element={<RentalPropertyMaintenancePage />} />
          <Route path="/realtor-services" element={<RealtorServicesPage />} />
          <Route path="/feedback/:token" element={<FeedbackPage />} />
          <Route path="/comeback/:token" element={<ComebackReschedulePage />} />
          <Route path="/heypaolo" element={<HeyPaolo />} />
          <Route path="/track/:token" element={<TrackPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/facility-maintenance" element={<FacilityMaintenancePage />} />
          <Route path="/commercial" element={<CommercialLandingPage />} />
          <Route path="/review" element={<ReviewRedirect />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/fall-ready" element={<FallReadyPage />} />
          <Route path="/winter-ready" element={<FallReadyPage />} />
          <Route path="/estimate" element={<EstimatorWizard />} />
          <Route path="/admin" element={<AdminEntry />} />
          <Route path="/app" element={<GetAppPage />} />
          <Route path="/admin/estimator" element={<AdminEstimator />} />
          <Route path="/estimate/scope/:id" element={<ScopeView />} />
          <Route path="/seasonal-flyer" element={<SeasonalFlyerPage />} />
          <Route path="/portal" element={<PortalPage />} />
          <Route path="/services/:slug" element={<LocalServicePage />} />
          <Route path="/analytics" element={<AnalyticsDashboard />} />
          
          {/* Blog */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          
          {/* Flyer - Printable */}
          <Route path="/flyer" element={<FlyerPage />} />
          
          {/* Legal Pages */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/sms-terms" element={<SmsTermsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/project/:slug" element={<ProjectPage />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/media-kit" element={<MediaKitPage />} />
          <Route path="/videos/:slug" element={<VideoPage />} />
          
          {/* Employee Routes */}
          <Route path="/employee/login" element={<Auth userType="employee" />} />
          <Route path="/admin/login" element={<Auth userType="employee" isAdminLogin={true} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/force-password-change" element={<ForcePasswordChangePage />} />
          <Route path="/employee/dashboard" element={<EmployeeDashboard />} />
          <Route path="/employee/job/:jobId/photos" element={<JobPhotos userType="employee" />} />
          <Route path="/employee/job/:jobId/invoice" element={<InvoiceCreator />} />
          <Route path="/invoices/new" element={<InvoiceCreator />} />
          <Route path="/invoice" element={<InvoiceCreator />} />
          <Route path="/invoices/:invoiceId" element={<InvoiceDetailPage />} />
          
          {/* Customer Routes */}
          <Route path="/customer/login" element={<Auth userType="customer" />} />
          <Route path="/customer/dashboard" element={<CustomerDashboard />} />
          <Route path="/customer/job/:jobId" element={<JobPhotos userType="customer" />} />
          <Route path="/approve/:token" element={<ApproveEstimatePage />} />
          <Route path="/pay/success" element={<PayResultPage />} />
          <Route path="/pay/cancel" element={<PayResultPage cancelled />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
        <PortalMascot variant="float" />
        <CallTextNow />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
